import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {
    CloudFunction,
    Flow,
    FlowSimulation,
    FlowState,
    MessageSource,
    SimulationId
} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {Slot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/Slot';
import {SlotId} from '@mintdata/mint-kit/api/models/Identifiers';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {action} from 'mobx';

export enum FlowExecType
{
    REST = 'REST',
    Stream = 'Stream',
}

//todo: this seems ugly.. change to some interface ?
export type LoggerFn = (simulationId:string, message:string) => void;
export type LoggerFnCb = (simulationId:string, message:string) => (arg?:any) => any;

function createLoggerFnCb(loggerFn:LoggerFn):LoggerFnCb
{
    return (id:SimulationId, msg:string) => (arg?:any) =>
    {
        if( loggerFn )
            loggerFn(id, msg);

        return arg;
    };
}

export class FlowController
{
    public getFlows():Flow[]
    {
        return this._getState().flows;
    }

    @action
    public setFlows(flows:Flow[]):void
    {
        this._getState().flows = flows;
    }

    @action
    public clearFlows():void
    {
        this._getState().flows = [];
    }

    public getFlowById(flowId:number):Flow | null
    {
        return this._getState().flows.find(({id}) => id === flowId);
    }

    public setCloudFunctions(cloudFunctions:CloudFunction[]):void
    {
        this._getState().cloudFunctions = cloudFunctions;
    }

    // @computed
    public getCloudFunctions():CloudFunction[]
    {
        const state = this._getState();

        if( state.cloudFunctions )
            return state.cloudFunctions;

        return state.flows.map((flow) => flow.cloudFunction);
    }

    protected _getState():FlowState
    {
        return rootState.ui.visualDesigner.flow;
    }

    @action
    public execCloudFunctionFromSlot(slot:Slot,
                                     functionId:number,
                                     outputComponentId:string,
                                     parameterValues:IFrame,
                                     execType:FlowExecType = FlowExecType.REST):Promise<any>
    {
        const {flowSimulation, flowSimulationStatus, flowSimulationMessages, flowCloud} = commonControllers;

        if( execType === FlowExecType.REST )
        {
            return flowCloud.executeFunction(functionId, IdUtil.newGUID(), parameterValues);
        }
        else if( execType === FlowExecType.Stream )
        {
            let simulation = flowSimulation.getForSlot(slot.id);

            //simulation is active => return immediately
            if( simulation && !flowSimulation.isStoppedOrInitialNotRunning(simulation.id) )
                return;

            if( !simulation )
            {
                simulation = FlowSimulation.forSlot(slot, functionId, outputComponentId);
                flowSimulation.add(simulation);
            }
            else
            {
                simulation.regenerateId();
            }

            simulation.execType = execType;

            const simulationId = simulation.id;

            flowSimulationStatus.onUserStartingTopology(simulationId);
            flowSimulationMessages.clearMessages(simulationId);

            return flowCloud.executeStream(functionId, simulationId, parameterValues)
                .then((response:{success:boolean}) =>
                {
                    if( response && !response.success )
                    {
                        flowSimulationStatus.onFailedToStartErrors(simulationId);
                        throw new Error('Flow execution unknown error');
                    }
                })
                .catch((err) =>
                {
                    flowSimulationMessages.logError(simulationId, `An error occurred during flow startup. ${err ? err : ''}`, MessageSource.UI);

                    if( !flowSimulation.isStoppedOrInitialNotRunning(simulationId) )
                    {
                        flowSimulationStatus.onFailedToStartErrors(simulationId);
                    }
                });
        }
    }

    @action
    public stopCloudFunctionFromSlot(slotId:SlotId):Promise<boolean>
    {
        const {flowSimulation} = commonControllers;

        const simulation = flowSimulation.getForSlot(slotId);
        if( !simulation )
            return Promise.resolve(false);

        return this.stopCloudFunctionExecution(simulation.id);
    }

    @action
    protected stopCloudFunctionExecution(executionId:string, logMsg?:LoggerFn, logErr?:LoggerFn):Promise<boolean>
    {
        const logMsgFnCb = createLoggerFnCb(logMsg);
        const logErrFnCb = createLoggerFnCb(logErr);

        const {flowSimulation, flowSimulationStatus, flowCloud} = commonControllers;

        const simulation = flowSimulation.getById(executionId);
        if( !simulation )
            return Promise.resolve(false);

        const simulationId = simulation.id;

        if( flowSimulation.isStoppedOrInitialNotRunning(simulationId) )
            return Promise.resolve(false);

        const simulationStatus = flowSimulation.getSimulationStatus(simulationId);

        flowSimulationStatus.onUserStoppingTopology(simulationId);

        // todo: Cloud function (flow) should be considered to be stopped after TopologyStatus.Stopping | TopologyStatus.Terminated message is received
        return Promise.resolve()
            .then(logMsgFnCb(simulationId, 'Stopping flow...'))
            .then(() =>
            {
                return flowCloud.stopStream(simulation.functionId, simulationId);
            })
            .then(() => flowSimulationStatus.onUserStoppedTopology(simulationId))
            .then(logMsgFnCb(simulationId, 'Stopping flow... done'))
            .then(() => true)
            .catch((err) =>
            {
                logErr && logErr(simulation.id, `An error occurred during flow stop phase. ${err ? err : ''}`);
                flowSimulationStatus.setStatus(simulationId, simulationStatus);
                return false;
            });
    }
}
